"use client";

import {
  Box,
  Button,
  ComboboxItem,
  Flex,
  Grid,
  MultiSelect,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { getHotkeyHandler, useClipboard, useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { IconCheck, IconCopy, IconInfoCircle } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import {
  repositoryMdm,
  repositoryPos,
} from "../../../../_base/_const/_constVar";
import { NotificationExtension } from "../../../../_base/extension/NotificationExtension";
import { MessageResponse } from "../../../../model/MessageResponse";
import { getProvinceSelectNew } from "../../../../service/getSelectApi";

export interface ConfigDeliveryXfastModel {
  code: string | null;
  name: string | null;
  att1: string | null;
  att3: string | null;
  attr16: string;
}

export const fieldTooltipsXfast: Record<string, string> = {
  code: "Mã định danh cấu hình giao vận Xfast (VD: XFAST_HN). Dùng trong code để phân biệt loại.",
  name: "Tên hiển thị cho khách hàng khi chọn phương thức giao hàng Xfast.",
  att1: "Danh sách tỉnh/TP mà cấu hình Xfast này áp dụng.",
  att3: "Danh sách phường/xã nội thành thuộc các tỉnh/TP đã chọn ở trên.",
};

export const renderLabelXfast = (
  label: string,
  fieldKey: string,
  required?: boolean,
) => {
  const tip = fieldTooltipsXfast[fieldKey];
  const asterisk = required ? (
    <span style={{ color: "var(--mantine-color-error)" }}>{" *"}</span>
  ) : null;
  if (!tip)
    return (
      <>
        {label}
        {asterisk}
      </>
    );
  return (
    <Flex
      component="span"
      align="center"
      gap={4}
      style={{ display: "inline-flex", verticalAlign: "middle" }}
    >
      <span>
        {label}
        {asterisk}
      </span>
      <Tooltip label={tip} multiline w={300} withArrow position="top">
        <IconInfoCircle
          size={14}
          color="#868e96"
          style={{ cursor: "pointer" }}
        />
      </Tooltip>
    </Flex>
  );
};

const CreateConfigDeliveryXfast = ({ onSuccess }: { onSuccess: any }) => {
  const clipboard = useClipboard({ timeout: 1000 });
  const [visible, { open, close }] = useDisclosure(false);
  const [dataProvinceSelectNew, setDataProvinceSelectNew] = useState<
    ComboboxItem[]
  >([]);
  const [dataCommuneSelectNew, setDataCommuneSelectNew] = useState<
    ComboboxItem[]
  >([]);

  const form = useForm<ConfigDeliveryXfastModel>({
    mode: "uncontrolled",
    validateInputOnChange: true,
    initialValues: {
      code: null,
      name: null,
      att1: null,
      att3: null,
      attr16: "Khác",
    },

    validate: {
      code: (value: string | null) => {
        if (!value || value.length === 0) {
          return "Vui lòng nhập mã loại GV !";
        }
      },
      name: (value: string | null) => {
        if (!value || value.length === 0) {
          return "Vui lòng nhập tên loại GV !";
        }
      },
    },
  });

  const handlePasteValueCommune = async () => {
    const text = await navigator.clipboard.readText();

    const data = text + "," + (form.getValues().att3 || "");

    const dataPaste = [
      ...new Set(
        data
          .split(",")
          .map((item) => item.trim())
          .filter((item) => item !== ""),
      ),
    ];

    const dataCommune = dataCommuneSelectNew.map((item: any) => item.value);
    const dataFinal = dataPaste.filter((i) => dataCommune.includes(i));

    form.setValues((prev) => ({
      ...prev,
      att3: dataFinal.join(","),
    }));

    if (dataPaste.length !== dataFinal.length) {
      NotificationExtension.Info("Đã lọc bỏ các phường/xã không hợp lệ !");
    }
  };

  const fetchDataProvinceNew = async () => {
    const res = await getProvinceSelectNew();

    if (res) {
      setDataProvinceSelectNew(
        res
          ?.filter((item) => item.id && item.name)
          ?.map((item) => ({
            value: String(item.id),
            label: String(item.name),
          })),
      );
    }
  };

  const fetchDataCommuneNew = async (listId: string[]) => {
    const url = listId.map((i) => `ProvinceIds=${i}`).join("&");

    const res = await repositoryMdm.get<MessageResponse<any>>(
      "/api/v1/TblDmCommuneMoi/get-select?active=true&" + url,
    );

    if (res && res.success) {
      const data = res.data
        ?.filter((item: any) => item.text && item.value)
        ?.map((item: any) => ({
          value: String(item.value),
          label: String(item.text),
        }));
      setDataCommuneSelectNew(data);

      if (form.getValues().att3 && String(form.getValues().att3)?.length > 0) {
        const dataForm = String(form.getValues().att3)?.split(",");
        const dataNew = data.map((item: any) => item.value);
        const dataSet = dataForm.filter((i) => dataNew.includes(i));

        form.setValues((prev) => ({
          ...prev,
          att3: dataSet.join(","),
        }));
      }
    }
  };

  const handleCreateConfigDeliveryXfast = async (
    dataSubmit: ConfigDeliveryXfastModel,
  ) => {
    open();
    try {
      const res = await repositoryPos.post<MessageResponse<any>>(
        "/api/v1/WebsiteDelivery/create",
        dataSubmit,
      );

      if (res && res.success) {
        NotificationExtension.Success("Tạo mới loại GV thành công !");
        setTimeout(() => {
          modals.closeAll();
          onSuccess(dataSubmit);
        }, 1000);
      }
    } catch (error) {
      console.log(error);
    } finally {
      close();
    }
  };

  useEffect(() => {
    fetchDataProvinceNew();
  }, []);

  const selectedProvinceValues =
    form.getValues().att1 && String(form.getValues().att1).length > 0
      ? String(form.getValues().att1).split(",")
      : [];
  const isAllProvinceSelected =
    dataProvinceSelectNew.length > 0 &&
    selectedProvinceValues.length === dataProvinceSelectNew.length;

  const selectedCommuneValues =
    form.getValues().att3 && String(form.getValues().att3).length > 0
      ? String(form.getValues().att3).split(",")
      : [];
  const isAllCommuneSelected =
    dataCommuneSelectNew.length > 0 &&
    selectedCommuneValues.length === dataCommuneSelectNew.length;

  return (
    <Box
      component="form"
      w={"50vw"}
      maw={700}
      pt={10}
      onSubmit={form.onSubmit((e: ConfigDeliveryXfastModel) =>
        handleCreateConfigDeliveryXfast(e),
      )}
    >
      <Grid>
        <Grid.Col span={{ base: 12, md: 6, lg: 6 }}>
          <TextInput
            key={form.key("code")}
            label={renderLabelXfast("Mã loại", "code", true)}
            placeholder="Nhập mã loại"
            {...form.getInputProps("code")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6, lg: 6 }}>
          <TextInput
            key={form.key("name")}
            label={renderLabelXfast("Tên loại", "name", true)}
            placeholder="Nhập tên loại"
            {...form.getInputProps("name")}
          />
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col span={12}>
          <MultiSelect
            label={renderLabelXfast("Tỉnh/TP áp dụng", "att1")}
            styles={{ input: { maxHeight: 110, overflowY: "auto" } }}
            placeholder="Nhập tỉnh/TP áp dụng"
            data={[{ label: "Tất cả", value: "ALL" }, ...dataProvinceSelectNew]}
            value={
              isAllProvinceSelected
                ? ["ALL", ...selectedProvinceValues]
                : selectedProvinceValues
            }
            searchable
            clearable
            nothingFoundMessage="Không tìm thấy dữ liệu !"
            {...form.getInputProps("att1")}
            onChange={(e) => {
              const allProvinceValues = dataProvinceSelectNew.map(
                (item) => item.value,
              );
              const clickedAll = e.includes("ALL");

              let finalValues: string[];
              if (clickedAll && !isAllProvinceSelected) {
                // Chọn "Tất cả" -> chọn hết các tỉnh/TP
                finalValues = allProvinceValues;
              } else if (
                !clickedAll &&
                isAllProvinceSelected &&
                e.length === allProvinceValues.length
              ) {
                // Bỏ chọn "Tất cả" -> bỏ hết
                finalValues = [];
              } else {
                finalValues = e.filter((v) => v !== "ALL");
              }

              form.setValues((prev) => ({
                ...prev,
                att1: finalValues.length > 0 ? finalValues.join(",") : null,
              }));

              if (finalValues.length > 0) {
                fetchDataCommuneNew(finalValues);
              }
            }}
          />
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col span={12}>
          <MultiSelect
            label={renderLabelXfast("Phường/xã áp dụng", "att3")}
            styles={{ input: { maxHeight: 220, overflowY: "auto" } }}
            placeholder="Nhập phường/xã áp dụng"
            data={[{ label: "Tất cả", value: "ALL" }, ...dataCommuneSelectNew]}
            disabled={
              !form.getValues().att1 ||
              String(form.getValues().att1).length === 0
            }
            rightSection={
              form.getValues().att3 &&
              String(form.getValues().att3).length > 0 && (
                <Tooltip label={clipboard.copied ? "Đã sao chép" : "Sao chép"}>
                  <IconCopy
                    size={16}
                    color={clipboard.copied ? "#15aabf" : "gray"}
                    onClick={() => {
                      clipboard.copy(form.getValues().att3);
                    }}
                  />
                </Tooltip>
              )
            }
            searchable
            clearable
            nothingFoundMessage="Không tìm thấy dữ liệu !"
            {...form.getInputProps("att3")}
            value={
              isAllCommuneSelected
                ? ["ALL", ...selectedCommuneValues]
                : selectedCommuneValues
            }
            onChange={(e) => {
              const allCommuneValues = dataCommuneSelectNew.map(
                (item) => item.value,
              );
              const clickedAll = e.includes("ALL");

              let finalValues: string[];
              if (clickedAll && !isAllCommuneSelected) {
                finalValues = allCommuneValues;
              } else if (
                !clickedAll &&
                isAllCommuneSelected &&
                e.length === allCommuneValues.length
              ) {
                finalValues = [];
              } else {
                finalValues = e.filter((v) => v !== "ALL");
              }

              form.setValues((prev) => ({
                ...prev,
                att3: finalValues.length > 0 ? finalValues.join(",") : null,
              }));
            }}
            onKeyDown={getHotkeyHandler([
              ["mod+V", handlePasteValueCommune],
              ["mod+C", () => clipboard.copy(form.getValues().att3)],
            ])}
          />
        </Grid.Col>
      </Grid>
      <Flex mt={10} justify={"end"} gap={"xs"}>
        <Button
          leftSection={<IconCheck size={16} />}
          color="green"
          type="submit"
          loading={visible}
        >
          Lưu
        </Button>
      </Flex>
    </Box>
  );
};

export default CreateConfigDeliveryXfast;
